import { CSVLink } from 'components/CSVLink';
import UncontrolledPopover from 'components/SafeUncontrolledPopover';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';
import React, {
  FC,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Link, useLocation, withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Card, CardBody, Col, Input, Label, Row } from 'reactstrap';
import { ReduxState } from 'types';
import { expandDashboardData } from 'views/Widgets/Dashboards/TeamDashboard/helpers';
import { getIconForFieldName } from 'views/Widgets/People/Filters/common';
import ConfirmAPI from '../../utils/api/ConfirmAPI';
import { sortRelationshipsByPriority } from '../../utils/models/Performance';
import {
  isCurrentEmployee,
  peopleObjectsAreEqual,
} from '../../utils/models/Person';
import { RELATIONSHIP_TYPES } from '../../utils/models/RelationshipUtils';
import { loadOrRender } from '../../utils/util/formatter';
import { createCSVFileName, sortByFieldFunc } from '../../utils/util/util';
import Page404 from '../Layout/Pages/Errors/Page404';
import Page from '../Layout/Pages/Page';
import ValidatedForm from '../Widgets/Forms/ValidatedForm';
import ReactTagsInput from '../Widgets/Inputs/ReactTagsInput';
import Loading from '../Widgets/Loading';
//import * as consts from '../../consts/consts';
import Avatar from '../Widgets/People/Avatar';
import AvatarGroup from '../Widgets/People/AvatarGroup';

const filterAbbreviationMap = {
  t: 'title',
  p: 'people',
  m: 'manager',
  a: 'manager_or_above',
};

const getFilterAbbreviationForType = (fieldName) => {
  for (const key in filterAbbreviationMap) {
    if (fieldName === filterAbbreviationMap[key]) {
      return key;
    }
  }

  return undefined;
};

const getFilterTypeForAbbreviation = (abbr) => {
  return filterAbbreviationMap[abbr];
};

const getFilterSerializedId = (filter) => {
  // for people, manager, and manager_or_above, use external_id if it exists
  if (
    filter._index === 'people' ||
    filter._index === 'manager' ||
    filter._index === 'manager_or_above'
  ) {
    return filter?.object?.external_id;
  }

  // for everything else, use the string itself (the name)
  return filter?.name;
};

const getDescriptionForFieldName = (fieldName) => {
  switch (fieldName) {
    case 'title':
      return 'Title';
    case 'people':
    case 'manager':
    case 'manager_or_above':
      // allow for PersonCard avatar icon to take precedence
      return undefined;
  }

  return fieldName;
};

const getPersonFromPeopleDataset = (
  campaign,
  surveyResponsePerson,
  configs,
  managementTier,
  incomingRelationships,
  outgoingRelationships
) => {
  const person = surveyResponsePerson;

  // NOTE: these relationships are auto-generated when the dataset is saved
  const managers = outgoingRelationships
    .filter((r) => r.dataset && r.type === RELATIONSHIP_TYPES.REPORTS_TO)
    .map((r) => r.to_person);

  const manager = !(managers?.length > 0)
    ? null
    : {
        ...managers[0],
        external_id: configs?.manager_external_id,
        effective_date: configs?.manager_effective_date,
      };

  const believedManagers = outgoingRelationships
    .filter((r) => !r.dataset && r.type === RELATIONSHIP_TYPES.REPORTS_TO)
    .map((r) => r.to_person);

  const believedManager =
    believedManagers?.length > 0 ? believedManagers[0] : undefined;

  const previousManagers = outgoingRelationships
    .filter(
      (r) => !r.dataset && r.type === RELATIONSHIP_TYPES.USED_TO_REPORT_TO
    )
    .map((r) => r.to_person);

  const incomingPeers = incomingRelationships
    .filter(
      (r) =>
        r.dataset &&
        r.type === RELATIONSHIP_TYPES.IS_CHOSEN_TO_WRITE_PEER_FEEDBACK_FOR
    )
    .sort(sortRelationshipsByPriority)
    .map((r) => ({
      ...r.from_person,
    }));

  const outgoingPeers = outgoingRelationships
    .filter(
      (r) =>
        r.dataset &&
        r.type === RELATIONSHIP_TYPES.IS_CHOSEN_TO_WRITE_PEER_FEEDBACK_FOR
    )
    .sort(sortRelationshipsByPriority)
    .map((r) => r.to_person);

  const personIsParticipating =
    configs?.is_participating === false ? false : true;
  const personIsWritingPeerReviews =
    configs?.is_writing_peer_reviews === false ? false : true;
  const personIsRated = configs?.is_rated === false ? false : true;

  const output = {
    ...person,
    // change person and manager urls to point to "/performance" underneath the
    // user which has all the context for what they wrote and others wrote about them
    url: person.url, // + '/performance',
    full_url: process.env.REACT_APP_CONFIRM_APP_URL + person.url,
    manager: manager,
    manager_external_id: configs?.manager_external_id,
    manager_full_name: manager?.full_name,
    manager_email: manager?.email,
    manager_title: manager?.title,
    manager_effective_date: configs?.manager_effective_date,
    believed_manager: peopleObjectsAreEqual(manager, believedManager)
      ? undefined
      : believedManager,
    believed_manager_full_name: believedManager?.full_name,
    believed_manager_email: believedManager?.email,
    believed_manager_title: believedManager?.title,
    previous_managers: previousManagers,
    previous_managers_text: previousManagers
      ?.map((p) => p?.full_name)
      ?.join(', '),

    // use official title from dataset in display
    title: configs?.title ? configs.title : person.title,

    // incoming relationships
    incoming_peers: incomingPeers,
    incoming_peers_count: personIsParticipating ? incomingPeers?.length : -1,
    incoming_high_priority: incomingRelationships
      .filter((r) => r.type === RELATIONSHIP_TYPES.NEEDS_AS_HIGH_PRIORITY_PEER)
      .map((r) => r.from_person),
    incoming_used_to_report_to: incomingRelationships
      .filter((r) => r.type === RELATIONSHIP_TYPES.USED_TO_REPORT_TO)
      .map((r) => r.from_person),
    incoming_energized: incomingRelationships
      .filter((r) => r.type === RELATIONSHIP_TYPES.ENERGIZED_BY)
      .map((r) => r.from_person),
    incoming_advised: incomingRelationships
      .filter((r) => r.type === RELATIONSHIP_TYPES.ADVISED_BY)
      .map((r) => r.from_person),
    incoming_stakeholder: incomingRelationships
      .filter((r) => r.type === RELATIONSHIP_TYPES.HAS_AS_STAKEHOLDER)
      .map((r) => r.from_person),

    // outgoing relationships
    outgoing_peers: outgoingPeers,
    outgoing_peers_count:
      personIsParticipating && personIsWritingPeerReviews
        ? outgoingPeers?.length
        : -1,
    outgoing_high_priority: outgoingRelationships
      .filter((r) => r.type === RELATIONSHIP_TYPES.NEEDS_AS_HIGH_PRIORITY_PEER)
      .map((r) => r.to_person),
    outgoing_used_to_report_to: outgoingRelationships
      .filter((r) => r.type === RELATIONSHIP_TYPES.USED_TO_REPORT_TO)
      .map((r) => r.to_person),
    outgoing_energized: outgoingRelationships
      .filter((r) => r.type === RELATIONSHIP_TYPES.ENERGIZED_BY)
      .map((r) => r.to_person),
    outgoing_advised: outgoingRelationships
      .filter((r) => r.type === RELATIONSHIP_TYPES.ADVISED_BY)
      .map((r) => r.to_person),
    outgoing_stakeholder: outgoingRelationships
      .filter((r) => r.type === RELATIONSHIP_TYPES.HAS_AS_STAKEHOLDER)
      .map((r) => r.to_person),

    // if not present in dataset, set to true, else if set to dataset explicitly as false, acknowledge
    is_participating: personIsParticipating,
    is_writing_peer_reviews: personIsWritingPeerReviews,
    is_rated: personIsRated,

    status: configs?.status,

    management_tier: managementTier,
  };

  return output;
};

const csvHeaders = [
  { label: 'ID', key: 'external_id' },
  { label: 'First name', key: 'given_name' },
  { label: 'Last name', key: 'family_name' },
  { label: 'Email', key: 'email' },
  { label: 'Title', key: 'title' },
  { label: 'Confirm resume', key: 'full_url' },
  { label: 'Manager name', key: 'manager_full_name' },
  { label: 'Manager email', key: 'manager_email' },
  { label: 'Manager title', key: 'manager_title' },
  { label: 'Believed manager name', key: 'believed_manager_full_name' },
  { label: 'Believed manager email', key: 'believed_manager_email' },
  { label: 'Believed manager title', key: 'believed_manager_title' },
  { label: 'Previous managers', key: 'previous_managers_text' },
  { label: 'Status', key: 'status' },
];

const getItemFromField = (fieldName, fieldValue) => {
  const iconName = getIconForFieldName(fieldName);
  const item = {
    _index: fieldName,
    name: fieldValue,
    icon: iconName ? (
      <div className="avatar avatar-xs me-2">
        <span
          style={{ position: 'relative', top: '1px' }}
          className="avatar-title fs-lg bg-primary-soft rounded-circle text-primary"
        >
          <i className={iconName} />
        </span>
      </div>
    ) : undefined,
    description: getDescriptionForFieldName(fieldName),
    object: fieldValue,
  };

  if (fieldName === 'people') {
    return {
      ...item,
      // need to pass id for uniqueness checking
      id: fieldValue.id,
      name: fieldValue.full_name,
    };
  } else if (fieldName === 'manager') {
    return {
      ...item,
      name: fieldValue.full_name + "'s direct reports",
    };
  } else if (fieldName === 'manager_or_above') {
    return {
      ...item,
      name: fieldValue.full_name + "'s full team",
    };
  }

  return item;
};

const itemsForFieldNameAreEqual = (fieldName, a, b) => {
  if (!a && !b) {
    return true;
  }

  if (!a || !b) {
    return false;
  }

  if (
    fieldName === 'people' ||
    fieldName === 'manager' ||
    fieldName === 'manager_or_above'
  ) {
    return peopleObjectsAreEqual(a, b);
  }

  return a === b;
};

const personIsUnderManager = (person, manager, allPeople) => {
  if (!person?.manager || !manager) {
    return false;
  }

  // if anyone above the manager in the hierarchy of the person
  // is the manager passed in, return true
  let currentManager = person?.manager;
  while (currentManager) {
    if (peopleObjectsAreEqual(currentManager, manager)) {
      return true;
    }
    const foundManager = allPeople.find((p) =>
      peopleObjectsAreEqual(p, currentManager)
    );
    currentManager = foundManager?.manager;
  }

  return false;
};

const personMatchesFilter = (person, filter, allPeople) => {
  // for manager_or_above, traverse entire team
  if (filter._index === 'manager_or_above') {
    return personIsUnderManager(person, filter?.object, allPeople);
  } else if (filter._index === 'people') {
    return peopleObjectsAreEqual(person, filter?.object);
  } else {
    return itemsForFieldNameAreEqual(
      filter._index,
      person[filter._index],
      filter.object
    );
  }
};

const personMatchesFilters = (person, filters, allPeople) => {
  if (!(filters?.length > 0)) {
    return true;
  }

  // matching any filter is sufficient (this is an "OR" filter)
  return (
    filters.findIndex((f) => personMatchesFilter(person, f, allPeople)) !== -1
  );
};

const getManagementTier = (person, managerRelationships) => {
  let tier = 1;
  const managers = [];

  let currentManagerRelationships = managerRelationships.filter(
    (r) => person.id === r.from_person.id
  );

  while (currentManagerRelationships?.length > 0) {
    tier++;
    const currentManager = currentManagerRelationships[0].to_person;

    // @ts-expect-error
    if (managers.indexOf(currentManager) !== -1) {
      // avoid infinite loop
      break;
    }

    // @ts-expect-error
    managers.push(currentManager);
    currentManagerRelationships = managerRelationships.filter(
      (r) => currentManager.id === r.from_person.id
    );
  }

  return tier;
};

const addTeamData = (people) => {
  const output = people;

  return output;
};

const PerformanceAdministrationPeerSelection: FC<Props> = (props) => {
  const { formatMessage, formatNumber } = useIntl();

  const location = useLocation();

  const [campaign, setCampaign] = useState(undefined);
  const [showIneligiblePeople, setShowIneligiblePeople] = useState(true);
  const [isMounted, setIsMounted] = useState(false);
  const [data, setData] = useState<object | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState(null);
  const [filters, setFilters] = useState([]);
  const [filterTier, setFilterTier] = useState(null);
  const [sortByFields, setSortByFields] = useState([
    'given_name',
    'family_name',
  ]);
  const [sortAtoZ, setSortAtoZ] = useState(true);

  const people = useMemo(() => {
    if (data === undefined) {
      return undefined;
    }

    // @ts-expect-error
    if (!data?.campaign || !data?.survey_responses || !data?.relationships) {
      return [];
    }

    // @ts-expect-error
    setCampaign(data.campaign);

    // @ts-expect-error
    const activeRelationships = data.relationships.filter(
      (r) =>
        isCurrentEmployee(r?.to_person?.status) &&
        isCurrentEmployee(r?.from_person?.status)
    );

    // @ts-expect-error
    const peopleWithoutTeamData = data.survey_responses
      .map((sr) =>
        getPersonFromPeopleDataset(
          // @ts-expect-error
          data.campaign,
          sr.person,
          sr.configs,
          getManagementTier(
            sr.person,
            activeRelationships.filter(
              (r) =>
                r.dataset &&
                r.type === RELATIONSHIP_TYPES.REPORTS_TO &&
                // @ts-expect-error
                data?.survey_responses &&
                // @ts-expect-error
                data.survey_responses.findIndex((sr) =>
                  peopleObjectsAreEqual(sr.person, r.from_person)
                ) !== -1
            )
          ),
          activeRelationships.filter((r) =>
            peopleObjectsAreEqual(r.to_person, sr.person)
          ),
          activeRelationships.filter((r) =>
            peopleObjectsAreEqual(r.from_person, sr.person)
          )
        )
      )
      .sort((a, b) => sortByFieldFunc(a, b, sortByFields));

    return addTeamData(peopleWithoutTeamData);
  }, [data, sortByFields]);

  const toggleIneligible = useCallback(
    () => setShowIneligiblePeople(!showIneligiblePeople),
    [showIneligiblePeople]
  );

  const getTablePersonFromPerson = useCallback((person) => {
    const uniqueId = person.id ? 'p-' + person.id : 'e-' + person.external_id;

    return {
      person: (
        <Row>
          <Col className="py-0 pe-1 col-auto">
            <Avatar
              className="mt-2 me-2"
              size="xs"
              linked={person.id ? true : false}
              // open externally to avoid interrupting flow
              isExternalUrl={true}
              person={person}
            />
          </Col>
          <Col className="p-0">
            <div>
              {person.id ? (
                <a target="_blank" rel="noopener noreferrer" href={person.url}>
                  {person.full_name}
                </a>
              ) : (
                person.full_name
              )}
            </div>
            <div className="text-muted fw-normal">{person.title}</div>
          </Col>
        </Row>
      ),
      incoming_peers: (
        <>
          {person.is_participating && (
            <AvatarGroup size="xs" people={person.incoming_peers} />
          )}
          {!person.is_participating && (
            <>
              <span id={'row-peers-in-not-eligible-' + uniqueId}>
                <i className="fe fe-user-x text-muted" />
              </span>
              <UncontrolledPopover
                placement="left"
                trigger="hover"
                target={'row-peers-in-not-eligible-' + uniqueId}
              >
                <FormattedMessage
                  id="app.views.performance.performance_administration_peer_selection.not_eligible_to_participate"
                  defaultMessage="
                Not eligible to participate in this cycle
              "
                />
              </UncontrolledPopover>
            </>
          )}
        </>
      ),
      outgoing_peers: (
        <>
          {person.is_participating && person.is_writing_peer_reviews && (
            <AvatarGroup size="xs" people={person.outgoing_peers} />
          )}
          {(!person.is_participating || !person.is_writing_peer_reviews) && (
            <>
              <span id={'row-peers-out-not-eligible-' + uniqueId}>
                <i className="fe fe-user-x text-muted" />
              </span>
              <UncontrolledPopover
                placement="left"
                trigger="hover"
                target={'row-peers-out-not-eligible-' + uniqueId}
              >
                <FormattedMessage
                  id="app.views.performance.performance_administration_peer_selection.not_eligible_to_participate"
                  defaultMessage="
                Not eligible to participate in this cycle
              "
                />
              </UncontrolledPopover>
            </>
          )}
        </>
      ),
      manager: (
        <>
          {person.believed_manager && (
            <>
              <h4
                id={'believed-manager-' + uniqueId}
                className="fe fe-alert-circle text-danger position-absolute"
                style={{ marginLeft: '-1rem', marginTop: '0.3rem' }}
              ></h4>
              <UncontrolledPopover
                placement="left"
                trigger="hover"
                target={'believed-manager-' + uniqueId}
              >
                <FormattedMessage
                  id="app.views.performance.performance_administration_peer_selection.this_person_believes_their_manager_is"
                  defaultMessage="
                This person believes their manager is {br} <link>{manager}</link>."
                  values={{
                    br: <br />,
                    link: (chunks) => (
                      <Link
                        target="_blank"
                        rel="noopener noreferrer"
                        to={person.believed_manager.url}
                      >
                        {chunks}
                      </Link>
                    ),
                    manager: person.believed_manager.full_name,
                  }}
                />
              </UncontrolledPopover>
            </>
          )}
          <span>
            {!(person?.previous_managers?.length > 0) && (
              <>
                {person.manager && (
                  <Avatar
                    linked={person.manager.id ? true : false}
                    // open externally to avoid interrupting flow
                    isExternalUrl={true}
                    className="me-2"
                    size="xs"
                    person={person.manager}
                  />
                )}
              </>
            )}
            {person?.previous_managers?.length > 0 && (
              <AvatarGroup
                // open externally to avoid interrupting flow
                isExternalUrl={true}
                className="me-2"
                size="xs"
                people={[
                  person.manager
                    ? person.manager
                    : {
                        full_name: 'Unknown manager',
                      },
                  ...person.previous_managers,
                ]}
                hideAllButFirst={true} // show only first manager visually, rest are in a + menu
                popoverHeaderPrefix={
                  <div className="mb-2">
                    <FormattedMessage
                      id="app.views.performance.performance_administration_peer_selection.previous_managers_mentioned"
                      defaultMessage="Previous managers mentioned"
                    />
                  </div>
                }
              />
            )}
          </span>
        </>
      ),
      incoming: (
        <Row>
          {[
            ['incoming_high_priority', 'HPri'],
            ['incoming_used_to_report_to', 'Prv DRs'],
            ['incoming_stakeholder', 'Stk for'],
            ['incoming_advised', "Adv's"],
            ['incoming_energized', "Energ's"],
          ].map((field, index) => (
            <Col key={index}>
              <Row>
                <Col
                  className={
                    'small text-muted ' + (index === 0 ? 'pe-0' : 'px-0')
                  }
                >
                  {field[1]}
                </Col>
              </Row>
              <Row>
                <Col
                  className={index === 0 ? 'pe-0' : 'px-0'}
                  style={{ minHeight: '26px' }}
                >
                  {person[field[0]]?.length > 0 ? (
                    <AvatarGroup size="xs" people={person[field[0]]} />
                  ) : (
                    '-'
                  )}
                </Col>
              </Row>
            </Col>
          ))}
        </Row>
      ),
      outgoing: (
        <Row>
          {[
            ['outgoing_high_priority', 'HPri'],
            ['outgoing_used_to_report_to', 'Prv mgrs'],
            ['outgoing_stakeholder', 'Stks'],
            ['outgoing_advised', 'Adv. by'],
            ['outgoing_energized', 'Energ. by'],
          ].map((field, index) => (
            <Col key={index}>
              <Row>
                <Col
                  className={
                    'small text-muted ' + (index === 0 ? 'pe-0' : 'px-0')
                  }
                >
                  {field[1]}
                </Col>
              </Row>
              <Row>
                <Col
                  className={index === 0 ? 'pe-0' : 'px-0'}
                  style={{ minHeight: '26px' }}
                >
                  {person[field[0]]?.length > 0 ? (
                    <AvatarGroup size="xs" people={person[field[0]]} />
                  ) : (
                    '-'
                  )}
                </Col>
              </Row>
            </Col>
          ))}
        </Row>
      ),
    };
  }, []);

  const toggleSortByFields = useCallback(
    (fields) => {
      // map UI field to appropriate internal field
      if (fields[0] === 'person') {
        fields = ['given_name', 'family_name'];
      } else if (fields[0] === 'manager') {
        fields = ['manager_given_name', 'manager_family_name'];
      } else if (fields[0] === 'incoming_peers') {
        fields = ['incoming_peers_count'];
      } else if (fields[0] === 'outgoing_peers') {
        fields = ['outgoing_peers_count'];
      }

      if (isEqual(sortByFields, fields)) {
        setSortAtoZ(!sortAtoZ);
      } else {
        setSortByFields(fields);
      }
    },
    [sortAtoZ, sortByFields]
  );

  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
    };
  }, []);

  useEffect(() => {
    if (!isMounted) {
      return;
    }

    // fetch people from the backend based on your permissions
    // @ts-expect-error
    if (typeof people === 'undefined' && props.currentOrganization?.id) {
      ConfirmAPI.getUrlWithCache(
        '/performance/admin/get-peers',
        'performance-admin-get-peers',
        null, // this is too much data, don't cache it
        null,
        {
          // @ts-expect-error
          organization: props.currentOrganization?.id,
          // @ts-expect-error
          proxy: props.currentProxyPerson
            ? // @ts-expect-error
              props.currentProxyPerson.email
            : undefined,
        },
        (data) => {
          if (isMounted) {
            if (data) {
              setData(expandDashboardData(data, formatMessage, formatNumber));
            }
          }
        },
        (message) => {
          setErrorMessage(message);
        }
      );
    }
  }, [
    isMounted,
    people,
    // @ts-expect-error
    props.currentOrganization,
    sortByFields,
    // @ts-expect-error
    props.currentProxyPerson,
    formatMessage,
    formatNumber,
  ]);

  const filteredPeople = useMemo(() => {
    if (!(people?.length > 0)) {
      return [];
    }

    // filter out ineligible people if that toggle is off
    const peopleWithEligibilityAccountedFor = showIneligiblePeople
      ? people
      : people.filter((p) => p.is_participating);

    // first filter by tier if tier filter exists
    const tieredPeopleWithoutTags = filterTier
      ? peopleWithEligibilityAccountedFor.filter(
          (p) => p.management_tier === filterTier
        )
      : peopleWithEligibilityAccountedFor;

    // then filter by any filter tags set
    if (!(filters?.length > 0)) {
      return tieredPeopleWithoutTags;
    }

    const tieredPeople = tieredPeopleWithoutTags.filter((p) =>
      personMatchesFilters(p, filters, peopleWithEligibilityAccountedFor)
    );

    return tieredPeople;
  }, [filterTier, filters, people, showIneligiblePeople]);

  const sortedFilteredPeople = useMemo(() => {
    if (sortByFields) {
      const output = [...filteredPeople].sort((a, b) =>
        sortByFieldFunc(a, b, sortByFields)
      );
      return sortAtoZ ? output : [...output].reverse();
    } else {
      return filteredPeople;
    }
  }, [filteredPeople, sortAtoZ, sortByFields]);

  const tablePeople = useMemo(() => {
    if (!sortedFilteredPeople) {
      return null;
    }

    if (sortedFilteredPeople.length === 0) {
      return [];
    }

    return sortedFilteredPeople.map(getTablePersonFromPerson);
  }, [getTablePersonFromPerson, sortedFilteredPeople]);

  const maxTier = useMemo(
    () =>
      people
        ? people.reduce((max, p) => {
            if (p.management_tier > max) {
              return p.management_tier;
            } else {
              return max;
            }
          }, 0)
        : undefined,
    [people]
  );
  const minTier = useMemo(
    () =>
      people
        ? people.reduce((min, p) => {
            if (p.management_tier < min) {
              return p.management_tier;
            } else {
              return min;
            }
          }, maxTier)
        : undefined,
    [people, maxTier]
  );

  const tiersList = useMemo(() => {
    if (maxTier) {
      const tiers = [];
      for (let i = minTier; i <= maxTier; i++) {
        // @ts-expect-error
        tiers.push(i);
      }
      // 1 should be ground floor
      tiers.reverse();
      return tiers;
    } else {
      return undefined;
    }
  }, [maxTier, minTier]);

  // filter tag elements
  const tagsAreEqual = useCallback((a, b) => {
    if (a?._index !== b?._index) {
      return false;
    }

    if (
      a._index === 'people' ||
      a._index === 'manager' ||
      a._index === 'manager_or_above'
    ) {
      return peopleObjectsAreEqual(a, b);
    }

    return a === b;
  }, []);

  const suggestions = useMemo(() => {
    if (!(people?.length > 0)) {
      return [];
    }

    const suggestionLists = people.reduce(
      (acc, p) => {
        for (const key in acc) {
          if (key === 'people') {
            // pass in entire person object
            acc[key].push(p);
          } else if (key === 'manager_or_above') {
            // we extract this one from the manager field
            if (
              p['manager'] &&
              acc['manager_or_above'].findIndex((v) =>
                itemsForFieldNameAreEqual(key, v, p['manager'])
              ) === -1
            ) {
              acc[key].push(p['manager']);
            }
          } else {
            if (
              p[key] &&
              acc[key].findIndex((v) =>
                itemsForFieldNameAreEqual(key, v, p[key])
              ) === -1
            ) {
              acc[key].push(p[key]);
            }
          }
        }

        return acc;
      },
      {
        // NOTE: order is what takes precedence
        // in autocomplete dropdown when options are limited
        manager_or_above: [],
        manager: [],
        people: [],
        business_unit: [],
        cost_center: [],
        function: [],
        level: [],
        title: [],
      }
    );

    let newSuggestions = [];
    for (const i in suggestionLists) {
      newSuggestions = newSuggestions.concat(
        suggestionLists[i].map((s) => getItemFromField(i, s))
      );
    }

    return newSuggestions;
  }, [people]);

  const getFilterByAbbreviationAndType = useCallback(
    (filterTypeAbbreviation, filterId) => {
      const type = getFilterTypeForAbbreviation(filterTypeAbbreviation);
      return suggestions.find(
        // @ts-expect-error
        (s) => s._index === type && getFilterSerializedId(s) === filterId
      );
    },
    [suggestions]
  );

  const getFilterFromSerialization = useCallback(
    (str) => {
      if (!(str?.length > 2)) {
        return null;
      }

      // first character is filter type abbreviation
      const filterTypeAbbreviation = str[0];
      // second char is -, then remaining characters are id for filter
      const filterId = str.substr(2);
      return getFilterByAbbreviationAndType(filterTypeAbbreviation, filterId);
    },
    [getFilterByAbbreviationAndType]
  );

  const serializeFilter = useCallback((f) => {
    if (!f) {
      return null;
    }

    // for manager and manager_or_above, get external_id or id (whatever is available)
    const filterTypeAbbreviation = getFilterAbbreviationForType(f._index);
    const filterId = getFilterSerializedId(f);

    return filterTypeAbbreviation + '-' + filterId;
  }, []);

  // set filters ONCE when url loads
  const urlParams = useRef(new URLSearchParams(location.search));
  useEffect(() => {
    if (urlParams?.current) {
      setFilters(
        // @ts-expect-error
        urlParams.current
          .getAll('filters[]')
          .map(getFilterFromSerialization)
          .filter((f) => f)
      );
    }
  }, [getFilterFromSerialization]);

  const setFiltersAndUpdateUrl = useCallback(
    (tags) => {
      // when changing filters, remove any management tier set
      // (as UX is strange when you add filter and don't see folks,
      // HRBPs reported)
      setFilterTier(null);
      setFilters(tags);

      // update url with details (so it can be bookmarked)
      const serializedTags = tags.map((t) => ['filters', serializeFilter(t)]);
      const newParams = new URLSearchParams(serializedTags);
      const urlString = newParams.toString();

      // change url without reloading page (requires using window.history and
      // not useHistory/history.push)
      window.history.replaceState(
        null,
        // @ts-expect-error
        null,
        location.pathname +
          (urlString ? '?' + urlString.replace(/filters=/g, 'filters[]=') : '')
      );
    },
    [location.pathname, serializeFilter]
  );

  const tagsInput = useMemo(
    () => (
      <Col className="mb-4">
        <ReactTagsInput
          // @ts-expect-error
          className={props.className}
          // @ts-expect-error
          inputClassName={props.inputClassName}
          // @ts-expect-error
          name={props.name}
          tagsAreEqual={tagsAreEqual}
          value={filters}
          allowNew={false}
          suggestions={suggestions}
          placeholder={formatMessage({
            id: 'app.views.performance.performance_administration_peer_selection.placeholder.filter_by_name_or_anything_else',
            defaultMessage: 'Filter by name or anything else',
          })}
          callback={setFiltersAndUpdateUrl}
          useTagCards={true}
          //excludeTagMatchFunction={excludeTagMatchFunction}
          // @ts-expect-error
          showPlaceholderAfterFirstTag={props.showPlaceholderAfterFirstTag}
        />
      </Col>
    ),
    [
      filters,
      // @ts-expect-error
      props.className,
      // @ts-expect-error
      props.inputClassName,
      // @ts-expect-error
      props.name,
      // @ts-expect-error
      props.showPlaceholderAfterFirstTag,
      setFiltersAndUpdateUrl,
      suggestions,
      tagsAreEqual,
      formatMessage,
    ]
  );

  const tiersListInput = useMemo(
    () => (
      <>
        {/* @ts-expect-error */}
        {tiersList?.length > 1 && (
          <Col className="col-auto">
            <Row id="reporting-tier-label">
              <Col className="col-auto pe-0">
                <div className="text-muted mt-2 pt-1">
                  <i className="fe fe-share-2 me-2" />
                </div>
              </Col>
              <Col className="col-auto">
                <ul className="pagination mb-4">
                  {/* @ts-expect-error */}
                  {tiersList.map((i, index) => (
                    <li
                      key={index}
                      className={
                        'page-item' + (filterTier === i ? ' active' : '')
                      }
                      role="button"
                      onClick={() => setFilterTier(filterTier === i ? null : i)}
                    >
                      <span className="page-link">
                        {/* @ts-expect-error */}
                        {tiersList?.length - i + minTier}
                      </span>
                    </li>
                  ))}
                </ul>
              </Col>
            </Row>
            <UncontrolledPopover
              placement="top"
              trigger="hover"
              target={'reporting-tier-label'}
            >
              <FormattedMessage
                id="app.views.performance.performance_administration_peer_selection.reporting_tier"
                defaultMessage="
              Reporting tier (1 = ICs, 2 = managers of 1's, 3 =
              manager's of 2's, etc.)
            "
              />
            </UncontrolledPopover>
          </Col>
        )}
      </>
    ),
    [filterTier, minTier, tiersList]
  );

  const eligibilityToggle = useMemo(
    () => (
      <Col className="col-auto mt-2">
        <div
          id="show-ineligibile-people"
          className={
            // @ts-expect-error
            'form-check form-switch' + (props.switchLabel ? '' : ' me-n2')
          }
          onClick={toggleIneligible}
        >
          <Input
            name="ineligibile-switch"
            type="checkbox"
            checked={showIneligiblePeople}
            onChange={toggleIneligible}
            className={'form-check-input'}
          />
          <Label
            className="form-check-label"
            htmlFor="ineligibile-switch"
            role="button"
          >
            <div className="mt-1">
              <i className="fe fe-user-x text-muted"></i>
            </div>
          </Label>
        </div>
        <UncontrolledPopover
          placement="top"
          trigger="hover"
          target={'show-ineligibile-people'}
        >
          {showIneligiblePeople ? (
            <FormattedMessage
              id="app.views.performance.performance_administration_peer_selection.popover.ineligible_people.hide"
              defaultMessage="Click to hide people that are not eligible to participate in this cycle."
            />
          ) : (
            <FormattedMessage
              id="app.views.performance.performance_administration_peer_selection.popover.ineligible_people.show"
              defaultMessage="Click to show people that are not eligible to participate in this cycle."
            />
          )}
        </UncontrolledPopover>
      </Col>
    ),
    // @ts-expect-error
    [props.switchLabel, showIneligiblePeople, toggleIneligible]
  );

  const exportButton = useMemo(
    () => (
      <>
        {sortedFilteredPeople?.length > 0 && (
          <Col className="col-auto">
            <CSVLink
              className="btn btn-light"
              filename={createCSVFileName()}
              data={sortedFilteredPeople}
              headers={csvHeaders}
            >
              <i className="fe fe-upload me-2" />
              <FormattedMessage
                id="app.views.performance.performance_administration_peer_selection.export"
                defaultMessage="
              Export {sortedFilteredPeopleCount} people to CSV"
                values={{
                  sortedFilteredPeopleCount: sortedFilteredPeople.length,
                }}
              />
            </CSVLink>
          </Col>
        )}
      </>
    ),
    [sortedFilteredPeople]
  );

  const columns = useMemo(
    () => [
      {
        name: 'Person',
        field: 'person',
        popoverContent: 'Name, title, and link to resume',
      },
      { name: 'Peers in', field: 'incoming_peers' },
      { name: 'Peers out', field: 'outgoing_peers' },
      { name: 'Mgr', field: 'manager', popoverContent: 'Manager' },
      {
        name: 'Incoming relationships',
        field: 'incoming',
        popoverContent: 'Incoming relationships',
      },
      {
        name: 'Outgoing relationships',
        field: 'outgoing',
        popoverContent: 'Outgoing relationships',
      },
    ],
    []
  );

  const peopleTableBody = useMemo(
    () => (
      <>
        {tablePeople.length === 0 && (
          <div className="text-center mb-4">
            <span className="text-muted">
              <FormattedMessage
                id="app.views.performance.performance_administration_peer_selection.no_results"
                defaultMessage="No results found"
              />
            </span>
          </div>
        )}
        {tablePeople.length > 0 && (
          <>
            <Row>
              <Col>
                <Card>
                  <div
                    className="table-responsive"
                    data-list='{"valueNames": ["tables-row", "tables-first", "tables-last", "tables-handle"]}'
                  >
                    <table className="card-table table table-sm mb-0 calibration-table">
                      <thead>
                        <tr>
                          {columns.map((c, index) => (
                            <th
                              key={index}
                              scope="col"
                              role="button"
                              onClick={() => toggleSortByFields([c.field])}
                              // @ts-expect-error
                              className={c.className}
                            >
                              <span
                                id={'column-' + index}
                                className="text-muted list-sort"
                                data-sort="tables-row"
                              >
                                {c.name === 'Person'
                                  ? tablePeople?.length + ' people'
                                  : c.name}
                              </span>
                              {c.popoverContent && (
                                <UncontrolledPopover
                                  placement="top"
                                  trigger="hover"
                                  target={'column-' + index}
                                >
                                  {c.popoverContent}
                                </UncontrolledPopover>
                              )}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="list">
                        {tablePeople.map((tp, index) => (
                          <tr key={index}>
                            {columns.map((c, colIndex) => (
                              <Fragment key={colIndex}>
                                {colIndex === 0 && (
                                  <th scope="row">{tp[c.field]}</th>
                                )}
                                {colIndex !== 0 && (
                                  <td
                                    className={
                                      // @ts-expect-error
                                      c.className ? c.className : undefined
                                    }
                                  >
                                    {tp[c.field]}
                                  </td>
                                )}
                              </Fragment>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </>
    ),
    [columns, tablePeople, toggleSortByFields]
  );

  const peopleTable = useMemo(
    () => (
      <>
        {people?.length > 0 && (
          <>
            <Row>
              {tagsInput}
              {tiersListInput}
              {eligibilityToggle}
              {exportButton}
            </Row>
            {peopleTableBody}
          </>
        )}
      </>
    ),
    [
      eligibilityToggle,
      exportButton,
      people,
      peopleTableBody,
      tagsInput,
      tiersListInput,
    ]
  );

  const callback = useCallback(
    (data) => {
      if (data) {
        toast.success(
          formatMessage({
            id: 'app.views.performance.performance_administration_peer_selection.peer_regeneration_triggered',
            defaultMessage: 'Peer regeneration triggered!',
          })
        );
      }
    },
    [formatMessage]
  );

  const loadOrRenderOutput = loadOrRender(people, errorMessage);
  if (loadOrRenderOutput) {
    return loadOrRenderOutput;
  }

  if (campaign === null) {
    return <Page404 />;
  }

  return (
    // @ts-expect-error
    <Page title={campaign?.name + ' Perf Administration: Peer Selection'}>
      <Row>
        <Col>
          <ValidatedForm
            method="GET"
            url="/performance/admin/get-anonymous-survey-responses"
            // @ts-expect-error
            object={{ campaign: campaign?.id }}
            inputs={[]}
            callback={(data) => {
              if (data) {
                toast.success(
                  formatMessage({
                    id: 'app.views.performance.performance_administration_peer_selection.anonymous_responses_fetched',
                    defaultMessage: 'Confidential responses fetched!',
                  })
                );
              }
            }}
            submitText={formatMessage({
              id: 'app.views.performance.performance_administration_peer_selection.submit_text.get_anonymous_responses',
              defaultMessage: 'Get confidential responses',
            })}
          />
        </Col>
        <Col>
          <ValidatedForm
            method="POST"
            url="/performance/admin/regenerate-peers"
            // @ts-expect-error
            object={{ campaign: campaign?.id }}
            inputs={[]}
            callback={callback}
            submitText={formatMessage({
              id: 'app.views.performance.performance_administration_peer_selection.submit_text.regenerate_peers',
              defaultMessage: 'Regenerate peers',
            })}
          />
        </Col>
      </Row>
      {!people && <Loading />}
      {people && people.length === 0 && (
        <Card>
          <CardBody>
            <FormattedMessage
              id="app.views.performance.performance_administration_peer_selection.no_access"
              defaultMessage="
            You do not have access to this feature. To get access, contact the
            administrator for Confirm at your organization.
          "
            />
          </CardBody>
        </Card>
      )}
      {peopleTable}
    </Page>
  );
};

const PerformanceAdministrationPeerSelection_propTypes = {
  title: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  history: PropTypes.object.isRequired,
};

type Props = PropTypes.InferProps<
  typeof PerformanceAdministrationPeerSelection_propTypes
>;

const mapStateToProps = (state: ReduxState) => {
  const { currentOrganization, currentProxyPerson } = state;

  return {
    currentOrganization,
    currentProxyPerson,
  };
};

export default connect(mapStateToProps)(
  withRouter(React.memo(PerformanceAdministrationPeerSelection))
);
